import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoContentFreshness, seoRunContentFreshness } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Stat, Pill, Loading, DataTable } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/content-freshness")({
  head: () => ({ meta: [{ title: "Content freshness · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: FreshnessPage,
});

const STATE_TONE: Record<string, string> = { current: "good", due: "warn", failed: "bad", never_reviewed: "neutral" };
const STATE_LABEL: Record<string, string> = {
  current: "Reviewed",
  due: "Due for review",
  failed: "Review failed — retrying",
  never_reviewed: "Not yet reviewed",
};

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }) + " ET" : "—";

function FreshnessPage() {
  const load = useServerFn(seoContentFreshness);
  const run = useServerFn(seoRunContentFreshness);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-content-freshness"], queryFn: () => load() });
  const d = q.data as any;
  const pages = (d?.pages ?? []) as any[];
  const counts: Record<string, number> = pages.reduce((a: Record<string, number>, p) => ({ ...a, [p.state]: (a[p.state] ?? 0) + 1 }), {});

  const review = useMutation({
    mutationFn: () => run({ data: { origin: window.location.origin } }),
    onSuccess: (r: any) => {
      toast.success(`Reviewed ${r.reviewed} pages — ${r.passed} passed, ${r.failed.length} need attention.`);
      qc.invalidateQueries({ queryKey: ["seo-content-freshness"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "The review could not run."),
  });

  return (
    <SeoPage
      title="Content freshness"
      description="Every page is checked monthly. The public “Last reviewed” date only moves after a check fully passes; failed checks keep the old date and retry the next day."
      actions={
        <Button onClick={() => review.mutate()} disabled={review.isPending}>
          {review.isPending ? "Reviewing…" : "Review due pages now"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Reviewed" value={counts.current ?? 0} />
            <Stat label="Due" value={counts.due ?? 0} />
            <Stat label="Failed" value={counts.failed ?? 0} />
            <Stat label="Not yet reviewed" value={counts.never_reviewed ?? 0} />
          </div>

          <Panel title="Rates" subtitle="“Verified” is the last successful pricing check; “Changed” is when the prices themselves last moved.">
            <DataTable
              head={["Scenario", "Last verified", "Last changed"]}
              rows={((d?.rates?.snapshots ?? []) as any[]).map((s) => [s.scenario_key, fmt(s.as_of), fmt(s.rates_changed_at)])}
            />
          </Panel>

          <Panel title="Pages">
            <DataTable
              head={["Page", "Status", "First published", "Last reviewed", "Next check", "Problem"]}
              rows={pages.map((p) => [
                <span key="p" className="font-mono text-xs">{p.sample_path}</span>,
                <Pill key="s" tone={STATE_TONE[p.state]}>{STATE_LABEL[p.state] ?? p.state}</Pill>,
                p.first_published_on,
                fmt(p.last_reviewed_at),
                fmt(p.next_due_at),
                <span key="f" className="text-xs text-muted-foreground">{p.failure_reason ?? ""}</span>,
              ])}
            />
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
